import { captureException } from 'src/shared/helpers/captureException';
import random from 'lodash/random';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { navigationService } from 'src/config/rootService';
import { CareRecommendationRecommendation, CoachNode } from 'src/generated/webapp_gql';
import {
    getAdditionalInfoProvided,
    getUserInformation,
} from 'src/modules/user/selectors/userSelectors';
import { getUpcomingVideoSessions } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import { ContentBanner, ContentBannerProps } from '@modernhealth/vital-components';
import BannerImage1 from 'src/public/ds/illustrations/man-relaxed.svg';
import BannerImage2 from 'src/public/ds/illustrations/microbreaks.svg';
import BannerImage4 from 'src/public/ds/illustrations/self-compassion.svg';
import BannerImage3 from 'src/public/ds/illustrations/woman-relaxed.svg';

interface Props {
    providers: CoachNode[];
}

const fallbackBanners = [
    {
        image: BannerImage1,
        backgroundColor: '#171B76',
    },
    {
        image: BannerImage2,
        backgroundColor: '#6B43C1',
    },
    {
        image: BannerImage3,
        backgroundColor: '#006775',
    },
    {
        image: BannerImage4,
        backgroundColor: '#334EAF',
    },
];

const fallbackBannerToRender = fallbackBanners[random(0, fallbackBanners.length - 1)];

const FallbackCard: React.FC<Props> = ({ providers }) => {
    const { t } = useTranslation();

    const userInformation = useSelector(getUserInformation);
    const upcomingSessions = useSelector(getUpcomingVideoSessions);
    const additionalInfoProvided = useSelector(getAdditionalInfoProvided);

    const carePreference =
        userInformation.careRecommendations?.[0]?.recommendation ??
        CareRecommendationRecommendation.Coaching;

    const hasProviders = providers.length > 0;
    const hasUpcomingSessions = upcomingSessions.length > 0;
    const hasOnboarded = additionalInfoProvided;

    function buildOneOnOneBannerParams(): ContentBannerProps {
        if (hasUpcomingSessions) {
            return {
                title: t('dashboard:fallback_banner.header.one_one'),
                description: t('dashboard:upcoming_sessions', {
                    count: upcomingSessions.length,
                }),
            };
        } else if (!hasProviders) {
            return {
                title: t('dashboard:fallback_banner.header.one_one'),
                description: !hasOnboarded
                    ? t('dashboard:get_started')
                    : t('dashboard:get_started.onboarded'),
            };
        } else {
            return {
                title: t('dashboard:fallback_banner.header.one_one'),
                description: t('dashboard:schedule_a_session'),
            };
        }
    }

    function buildBannerParams(): ContentBannerProps {
        switch (carePreference) {
            // We don't support Courses care recommendation but need this as it still exist in type
            case CareRecommendationRecommendation.Courses:
            case CareRecommendationRecommendation.DigitalPrograms:
                return {
                    title: t('dashboard:fallback_banner.header.programs'),
                    description: t('dashboard:meditations_description'),
                    buttonTitle: t('dashboard:fallback_banner.cta.programs'),
                    onClick: () => {
                        const to = '/resources/topics';

                        navigationService.navigate(to);
                    },
                };
            case CareRecommendationRecommendation.Circles:
                return {
                    title: t('dashboard:fallback_banner.header.circles'),
                    description: t('dashboard:circles_description'),
                    buttonTitle: t('dashboard:see_upcoming_circles'),
                    onClick: () => {
                        window.location.href =
                            'https://circles.modernhealth.com?utm_source=modern-health&utm_medium=web-app';
                    },
                };
            case CareRecommendationRecommendation.Therapy:
            case CareRecommendationRecommendation.Coaching:
            case CareRecommendationRecommendation.MentalHealthCoaching:
                return buildOneOnOneBannerParams();
            default:
                // Check that we've branched all known members of the Recommendation enum...
                const exhaustivenessCheck: never = carePreference;
                captureException(
                    new Error(
                        'Unknown care recommendation passed to home screen, defaulting to 1:1 view.',
                    ),
                    { exhaustivenessCheck },
                );
                // Fallback to 1:1 view in the unknown case.
                return buildOneOnOneBannerParams();
        }
    }

    const { title, description, buttonTitle, onClick } = buildBannerParams();
    return (
        <ContentBanner
            image={fallbackBannerToRender.image}
            backgroundColor={fallbackBannerToRender.backgroundColor}
            title={title ?? ''}
            description={description ?? ''}
            buttonTitle={buttonTitle}
            onClick={onClick}
        />
    );
};

export default FallbackCard;
